import { useColorMode } from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isEmpty } from 'lodash';
import { t } from 'i18next';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { getArtistId, getArtistIdAnyway } from '../../utils/etcUtils';
import saveData from '../../utils/saveData';
import { openNewTab } from '../../utils/UIUtils';
import usePhotoCardEventsQuery from './hooks/usePhotoCardEventsQuery';
import useInvalidateQueries from './hooks/usePhotoCardQueryInvalidation ';
import useFilterPhotoCards from './hooks/useFilterPhotoCards';
import EmptyPhotoCardEventSkeletonV2 from '../../components/skeletons/EmptyPhotoCardEventSkeletonV2';
import EmptyCase from '../../components/errors/EmptyCase';
import PhotoCardsByEventViewer from './components/PhotoCardsByEventViewer';
import EmptyMyPhotoCardContent from './components/EmptyMyPhotoCardContent';
import PendingPhotoCardsSection from '../../components/photocard/PendingPhotoCardsSection';
import { ReportPhotoCardBox } from '../../components/report/ReportPhotoCardBox';
import HomeHeader from './components/HomeHeader';
import BoxTabs from '../../pages/tabs/BoxTabs';
import PhotoCardsSummaryRow from './components/PhotoCardsSummaryRow';
import OfflineLocationTab from '../../pages/tabs/OfflineLocationTab';
import HomePopFabButton from './components/HomePopFabButton';
import HomeBottomNav from '../../layout/HomeBottomNav';
import ChangePhotoCardCollectedCountBottomSheet from '../../components/bottomSheet/ChangePhotoCardCollectedCountBottomSheet';
import LikedMembersList from '../../components/lists/LikedMembersList';
import CardFillIcon from '../../components/icons/CardFillIcon';
import { ReactComponent as MapFillIcon } from '../../icons/map.fill.svg';
import useUserQuery from '../hooks/queries/useUserQuery';
import useRefreshJwtQuery from '../hooks/queries/useRefreshJwtQuery';
import useArtistQuery from '../hooks/queries/useArtistQuery';
import useArtistQueryLikedOnly from '../hooks/queries/useArtistQueryLikedOnly';
import useReportedPhotoCardsQuery from '../hooks/queries/useReportedPhotoCardsQuery';
import type { PhotoCardType } from '../types/PhotoCardType';
import type { ArtistType, MemberType } from '../types/ArtistType';

export default function CollectionsHomePage() {
  const { colorMode } = useColorMode();
  const navigate = useNavigate();
  const artistId = getArtistId();

  const [isShowChangeCollectCountBottomSheet, setIsShowChangeCollectCountBottomSheet] =
    useState(false);
  const [currentMember, setCurrentMember] = useState<MemberType | null>(null);
  const [selectedPhotoCard, setSelectedPhotoCard] = useState<PhotoCardType>();
  const [forceRender, setForceRender] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState<ArtistType | null>(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [isMyPhotoCardsOnly, setIsMyPhotoCardsOnly] = useState(false);

  // API 쿼리들
  useRefreshJwtQuery();
  const { data: user } = useUserQuery();
  const { data: artists } = useArtistQueryLikedOnly();
  const { data: artist } = useArtistQuery(artistId || '');
  const { data: reportedPhotoCards } = useReportedPhotoCardsQuery({
    user: user || null,
    artistId: artistId || selectedArtist?.artistId || '',
    onlyPending: true
  });
  const { data, isLoading, hasNextPage, isFetchingNextPage, loadMore } = usePhotoCardEventsQuery({
    artistId: selectedArtist?.artistId || artistId || '',
    memberId: currentMember?.memberId || '',
    likedEventOnly: isMyPhotoCardsOnly
  });
  const { updateCollectedCount } = useInvalidateQueries({
    artistId: artistId || '',
    currentMember: currentMember || null
  });
  const hasMyPhotoCards = (data?.pages?.[0]?.meta?.photoCardsInfo?.collectedCount || 0) > 0;
  const { processedEvents } = useFilterPhotoCards({
    data: data || null,
    hasMyPhotoCards,
    isMyPhotoCardsOnly
  });

  // 첫 페이지가 필터링되어 비어 있을 때 자동으로 다음 페이지를 로드 (likedEventOnly 케이스 보호)
  useEffect(() => {
    const noCards =
      processedEvents.length === 0 || processedEvents.every((event) => isEmpty(event.photoCards));

    if (isMyPhotoCardsOnly && noCards && hasNextPage && !isLoading && !isFetchingNextPage) {
      loadMore();
    }
  }, [isMyPhotoCardsOnly, processedEvents, hasNextPage, isLoading, isFetchingNextPage, loadMore]);

  // 내 포토카드가 없는 사용자 오버레이 케이스에서도 첫 페이지가 비어 있으면 자동으로 다음 페이지 로드
  useEffect(() => {
    if (hasMyPhotoCards) return;
    const noOverlayCandidates = processedEvents.length === 0;
    if (noOverlayCandidates && hasNextPage && !isLoading && !isFetchingNextPage) {
      loadMore();
    }
  }, [hasMyPhotoCards, processedEvents, hasNextPage, isLoading, isFetchingNextPage, loadMore]);

  // 요약 정보
  const photoCardSummary = useMemo(() => {
    return data?.pages?.[0]?.meta?.photoCardsInfo || null;
  }, [data]);

  const onLongClickedPhotoCard = (photoCard: PhotoCardType) => {
    setSelectedPhotoCard(photoCard);
    setIsShowChangeCollectCountBottomSheet(true);
  };

  const onChangeArtist = (artist: ArtistType) => {
    setSelectedArtist(artist);
    saveData('artist', artist.artistId);
    setForceRender(!forceRender);
  };

  const isShowFabButton = tabIndex === 0 && !isShowChangeCollectCountBottomSheet && hasMyPhotoCards;

  //조건별 포토카드 메인 컨텐츠 리턴
  const renderPhotoCardContent = () => {
    // 로딩 중일 때
    if (!processedEvents.length && isLoading) {
      return (
        <div className="px-[24px]">
          <EmptyPhotoCardEventSkeletonV2 />
        </div>
      );
    }

    // 모든 이벤트에 포토카드가 없고 더 이상 로드할 페이지가 없을 때
    if (processedEvents.every((event) => isEmpty(event.photoCards)) && !hasNextPage) {
      return (
        <div className="w-full px-[24px]">
          {/* @ts-expect-error */}
          <EmptyCase emoji={'🥺'} description={t('photoCardPage.emptyPhotoCards')} />
        </div>
      );
    }

    //포토카드가 있을때
    return (
      <div className="w-full h-full" style={{ position: 'relative' }}>
        <PhotoCardsByEventViewer
          events={processedEvents}
          onLongClickedPhotoCard={onLongClickedPhotoCard}
          endReached={loadMore}
          isNextPageLoading={isLoading || isFetchingNextPage}
        />
        {hasMyPhotoCards && <div className="mb-[30px]" />}
        {/* 내 포토카드가 없을 때 오버레이 */}
        {!hasMyPhotoCards && <EmptyMyPhotoCardContent />}
      </div>
    );
  };

  const renderBottomSection = () => {
    // 신고된 포토카드가 없을 때
    if (isEmpty(reportedPhotoCards)) {
      // 내 포토카드가 있다면 신고 박스 표시
      if (hasMyPhotoCards) {
        return (
          <div className="px-[24px] pt-[10px] pb-[205px]">
            <ReportPhotoCardBox artist={selectedArtist} member={currentMember} />
          </div>
        );
      }
      return null;
    }

    // 신고된 포토카드가 있을 때 대기 중인 포토카드 섹션 표시
    return (
      <div className="px-[24px] pt-[10px] pb-[200px]">
        <PendingPhotoCardsSection
          reportedPhotoCards={reportedPhotoCards}
          onViewAllClick={() => {
            navigate('/reported-photo-cards');
          }}
          onPhotoCardClick={(photoCard: PhotoCardType) => {
            openNewTab(`/photo_cards/${photoCard.photoCardId}`);
          }}
        />
      </div>
    );
  };

  return (
    <div className="pt-[env(safe-area-inset-top)] bg-scroll h-auto min-h-screen dark:bg-background-dark bg-background-light">
      <Helmet>
        <title>{t('tabs.collections')}</title>
        <meta name="theme-color" content={colorMode === 'dark' ? '#0D0E12' : '#FFFFFF'} />
      </Helmet>
      <HomeHeader artists={artists || []} onChangeArtist={onChangeArtist} />
      <div className="px-[24px] py-[4px]">
        <BoxTabs tabLabels={TabLabels} onTabSelected={setTabIndex} />
      </div>
      {tabIndex === 0 ? (
        <>
          <PhotoCardsSummaryRow
            totalCount={photoCardSummary?.totalCount || 0}
            collectedCount={photoCardSummary?.collectedCount || 0}
            isCheckedPhotoCardFilter={isMyPhotoCardsOnly}
            onChangePhotoCardFilter={() => {
              if (hasMyPhotoCards) {
                setIsMyPhotoCardsOnly(!isMyPhotoCardsOnly);
              }
            }}
            currentMember={currentMember || null}
          />
          <LikedMemberLists artist={artist || null} onChangedMember={setCurrentMember} />
          <div>{renderPhotoCardContent()}</div>
          {!isFetchingNextPage && !isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 1 }}
            >
              {renderBottomSection()}
            </motion.div>
          )}
        </>
      ) : (
        selectedArtist && (
          <OfflineLocationTab
            selectedArtist={{
              artistId: selectedArtist.artistId,
              fandomName: selectedArtist.fandomName
            }}
          />
        )
      )}
      {isShowFabButton && <HomePopFabButton selectedArtist={selectedArtist} />}
      {/* @ts-expect-error */}
      <HomeBottomNav tabIndex={0} artistId={getArtistIdAnyway(artist)} />

      {selectedPhotoCard && isShowChangeCollectCountBottomSheet && (
        <ChangePhotoCardCollectedCountBottomSheet
          isShowChangeCollectCountBottomSheet={isShowChangeCollectCountBottomSheet}
          setIsShowChangeCollectCountBottomSheet={setIsShowChangeCollectCountBottomSheet}
          currentCollectedCount={
            selectedPhotoCard.collectedCount > 0 ? selectedPhotoCard.collectedCount : 1
          }
          updatedCollectedCount={(collectedCount: number) =>
            updateCollectedCount(selectedPhotoCard, collectedCount)
          }
        />
      )}
    </div>
  );
}

const LikedMemberLists = ({
  artist,
  onChangedMember
}: {
  artist: ArtistType | null;
  onChangedMember: (member: MemberType | null) => void;
}) => {
  return (
    <div className="px-[24px] py-[6px] pt-0">
      {/* @ts-expect-error */}
      <LikedMembersList
        artistMembers={artist?.members?.filter((member) => member.isLiked)}
        onChangeLikedMember={onChangedMember}
      />
    </div>
  );
};

const TabLabels = [
  <div className="flex flex-row items-center gap-[2px] " key="PocapanTab">
    <CardFillIcon width={20} height={20} className="fill-main-light-3 dark:fill-main-dark-3" />
    <div className="font-semibold text-[15px] text-center text-main-light-2 dark:text-main-dark-3">
      {t('CollectionsHomePage.pocapanTab')}
    </div>
  </div>,
  <div className="flex flex-row items-center gap-[2px]" key="OffPanTab">
    <MapFillIcon width={20} height={20} className="fill-main-light-3 dark:fill-main-dark-3" />
    <div className="font-semibold text-[15px] text-center text-main-light-2 dark:text-main-dark-3">
      {t('CollectionsHomePage.offpanTab')}
    </div>
  </div>
];
